
import ISpPageContext from '../../interfaces/ISpPageContext';
import { ItemUrlHelper, SiteRelation, IItemUrlParts, IGetUrlPartsOptions } from './ItemUrlHelper';
import { IApiUrlBasic, ApiUrlBasic, ApiUrlHelperBasic } from './ApiUrlHelperBasic';

export * from './ApiUrlHelperBasic';

/**
 * Represents a builder for SharePoint API URLs.
 *
 * @export
 * @interface IApiUrl
 */
export interface IApiUrl extends IApiUrlBasic {
    /**
     * Appends segments to the URL to create a 'web' context, for the Web
     * which contains the given item, list, or web by URL.
     * Supply as many parts of the URL as are known, so that the correct web route
     * may be selected.
     *
     * @param {IGetUrlPartsOptions} [options]
     * @returns {this}
     *
     * @memberOf IApiUrl
     */
    webByUrl(options?: IGetUrlPartsOptions): this;

    /**
     * Appends segments to the URL to create a 'web' context, for the web
     * determined by parts extracted from an `ItemUrlHelper`.
     * Use this method instead of `webByUrl` if there is already an `IItemUrlParts`
     * object available when using an `ItemUrlHelper`.
     *
     * @param {IItemUrlParts} urlParts
     * @returns {this}
     *
     * @memberOf IApiUrl
     */
    webByItemUrl(urlParts: IItemUrlParts): this;

    /**
     * Appends segments to the URL to create a 'web' context for the item's origin list.
     *
     * @param {IItemUrlParts} urlParts
     * @param {string} listId
     * @returns {this}
     *
     * @memberOf IApiUrl
     */
    listById(urlParts: IItemUrlParts, listId: string): this;
}

export interface IApiUrlHelperParams {
    // Nothing
}

export interface IApiUrlHelperDependencies {
    pageContext: ISpPageContext;
    itemUrlHelper: ItemUrlHelper;
    apiUrlType?: new () => IApiUrl;
    encodeWebAbsoluteUrl?: boolean;
}

/**
 * Implementation of an extendable SharePoint API URL. Method on this class mutate the underlying object and return the same object back to the caller.
 */
export class ApiUrl extends ApiUrlBasic implements IApiUrl {
    private _pageContext: ISpPageContext;
    private _itemUrlHelper: ItemUrlHelper;
    private _shouldEncodeWebAbsoluteUrl: boolean;

    constructor(params: {}, dependencies: IApiUrlDependencies) {
        super({
            webAbsoluteUrl: dependencies.pageContext.webAbsoluteUrl,
            encodeWebAbsoluteUrl: dependencies.encodeWebAbsoluteUrl
        });

        this._pageContext = dependencies.pageContext;

        this._itemUrlHelper = dependencies.itemUrlHelper;
    }

    public webByUrl(options?: IGetUrlPartsOptions): this {
        return this.webByItemUrl(this._itemUrlHelper.getUrlParts(options));
    }

    public webByItemUrl(urlParts: IItemUrlParts): this {
        const {
            siteRelation,
            isCrossDomain,
            fullItemUrl
        } = urlParts;

        let apiUrl = this;

        if (isCrossDomain || siteRelation === SiteRelation.crossSite) {
            apiUrl = apiUrl.method('SP.RemoteWeb', fullItemUrl);
        }

        return apiUrl.segment('web');
    }

    public listById(urlParts: IItemUrlParts, listId: string): this {
        return this.webByItemUrl(urlParts).segment('lists').method('GetById', listId);
    }

    protected clone(): this {
        return <this>new ApiUrl({
            webAbsoluteUrl: this._pageContext.webAbsoluteUrl
        }, {
                pageContext: this._pageContext,
                itemUrlHelper: this._itemUrlHelper,
                encodeWebAbsoluteUrl: this._shouldEncodeWebAbsoluteUrl
            });
    }
}

/**
 * Component which assists in constructing SharePoint API URLs.
 *
 * @export
 * @class ApiUrlHelper
 *
 * @example
 *  import ApiUrlHelper, { resourceKey as apiUrlHelperKey } from './ApiUrlHelper';
 *  let apiUrlHelper = this.resources.consume(apiUrlHelperKey);
 *
 *  let apiUrl = apiUrlHelper.build()
 *      .webByItemKey(item.key)
 *      .segment('Folders')
 *      .segment('Add');
 *
 *  this._dataRequestor.getData({
 *      url: apiUrl.toString(),
 *      method: 'post',
 *      qosName: 'AddFolder'
 *  }).then(() => {
 *      // Something.
 *  });
 */
export class ApiUrlHelper extends ApiUrlHelperBasic {
    protected _apiUrlType: new () => IApiUrl;

    constructor(params: IApiUrlHelperParams, dependencies: IApiUrlHelperDependencies) {
        super({
            webAbsoluteUrl: dependencies && dependencies.pageContext ? dependencies.pageContext.webAbsoluteUrl : '',
            encodeWebAbsoluteUrl: dependencies.encodeWebAbsoluteUrl
        });

        const {
            itemUrlHelper,
            pageContext,
            encodeWebAbsoluteUrl
        } = dependencies;

        const {
            apiUrlType = class extends ApiUrl {
                constructor() {
                    super({}, {
                        itemUrlHelper: itemUrlHelper,
                        pageContext: pageContext,
                        encodeWebAbsoluteUrl: encodeWebAbsoluteUrl
                    });
                }
            }
        } = dependencies;

        this._apiUrlType = apiUrlType;
    }

    /**
     * Starts building a new SharePoint API URL.
     *
     * @returns {IApiUrl}
     */
    public build(): IApiUrl {
        return new this._apiUrlType();
    }
}

export interface IApiUrlDependencies {
    readonly itemUrlHelper: ItemUrlHelper;
    readonly pageContext: ISpPageContext;
    readonly encodeWebAbsoluteUrl?: boolean;
}
